import { Box, Button, Tooltip, Typography } from "@mui/joy";
import { heightWithToolbar } from "global";

import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-buttons/styles/material.css";
import "@syncfusion/ej2-calendars/styles/material.css";
import "@syncfusion/ej2-dropdowns/styles/material.css";
import "@syncfusion/ej2-gantt/styles/material.css";
import "@syncfusion/ej2-grids/styles/material.css";
import "@syncfusion/ej2-inputs/styles/material.css";
import "@syncfusion/ej2-layouts/styles/material.css";
import "@syncfusion/ej2-lists/styles/material.css";
import "@syncfusion/ej2-navigations/styles/material.css";
import "@syncfusion/ej2-notifications/styles/material.css";
import "@syncfusion/ej2-popups/styles/material.css";
import {
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GanttComponent,
  Inject,
  Selection,
  Toolbar,
  PdfExport,
  DayMarkers,
  ExcelExport,
  EventMarkersDirective,
  EventMarkerDirective,
} from "@syncfusion/ej2-react-gantt";
import "@syncfusion/ej2-richtexteditor/styles/material.css";
import "@syncfusion/ej2-treegrid/styles/material.css";
import moment from "moment";
import { useEffect, useRef } from "react";
import { useProjects } from "redux/project/projectHooks";
import { useTags } from "redux/tag/tagHooks";
import { useActiveWorkSessions } from "redux/workSession/workSessionHooks";
import { Link, useNavigate } from "react-router-dom";
import { RouterConfig } from "hooks/AppRouter/AppRouter";
import { WorkSession } from "entities/workSession";
import { Project } from "entities/project";
import { Tag } from "entities/tag";
import { HiringProgress } from "features/dataGrids/ProjectsDataGrid/ProjectsDataGrid";
import { useStages } from "redux/stage/stageHooks";
import { Dictionary } from "@reduxjs/toolkit";
import { Stage } from "entities/stage";
/**
 *
 * @returns {ReactElement} GanttPage page
 */
export function GanttPage() {
  const { activeWorkSessions } = useActiveWorkSessions();
  const { projects: allProjects } = useProjects();
  const { stagesMap } = useStages();
  const { tags } = useTags();
  const ref = useRef();
  const navigate = useNavigate();
  const taskFields = {
    id: "TaskID",
    name: "TaskName",
    data: "Data",
    startDate: "StartDate",
    endDate: "EndDate",
    child: "subtasks",
  };
  const project = allProjects.filter((p) => p.version >= 2 && !p.archived);
  const projectWithoutTag = project.filter((p) => !p.tagID);

  const labelSettings: any = {
    leftLabel: (props) => {
      if (props.TaskID.includes("WorkSession")) {
        const data = props.taskData.Data as WorkSession;
        return (
          <Tooltip
            arrow
            variant="outlined"
            title={<TooltipContent workSession={data} />}
          >
            <Typography
              startDecorator={<i className={data?.typeOption()?.icon} />}
              level="body2"
            >
              {props.TaskName}
            </Typography>
          </Tooltip>
        );
      }
      if (props.TaskID.includes("Project")) {
        const data = props.taskData.Data as Project;
        return (
          <Tooltip
            arrow
            variant="outlined"
            title={<TooltipContent project={data} stagesMap={stagesMap} />}
          >
            <Typography
              startDecorator={
                data.logoURL ? (
                  <img src={data.logoURL} style={{ width: 25, height: 25 }} />
                ) : (
                  <i className="fa-solid fa-folder-open"></i>
                )
              }
              level="body2"
            >
              {props.TaskName}
            </Typography>
          </Tooltip>
        );
      }
      if (props.TaskID.includes("Tag")) {
        const data = props.taskData.Data as Tag;
        return (
          <Typography
            startDecorator={
              data.image ? (
                <img src={data.image} style={{ width: 25, height: 25 }} />
              ) : (
                <i className="fa-solid fa-ticket"></i>
              )
            }
            level="body2"
          >
            <b>{props.TaskName}</b>
          </Typography>
        );
      }
    },
  };

  useEffect(() => {
    if (ref.current) {
      (ref as any).current?.fitToProject();
    }
  }, [ref]);

  const data = [];

  tags.forEach((t) => {
    const tagProjects = project.filter((p) => p.tagID === t.id);
    data.push({
      TaskID: `Tag_${t.id}`,
      Color: "#ff9800",
      Data: t,
      TaskName: t.name,
      subtasks: tagProjects.map((p) => {
        let projectWorkSessions = activeWorkSessions.filter(
          (w) => w.projectID === p.id
        );
        projectWorkSessions = projectWorkSessions.filter((w) => !w.isTBD());
        projectWorkSessions.sort(
          (a, b) =>
            moment(a.dateFromUTC).valueOf() - moment(b.dateFromUTC).valueOf()
        );
        return {
          TaskID: `Project_${p.id}`,
          Data: p,
          Color: "#2196f3",
          TaskName: p.name,
          subtasks: projectWorkSessions.map((w) => ({
            TaskID: `WorkSession_${w.id}`,
            TaskName: `${w.title}`,
            Data: w,
            Color: "#4caf50",
            StartDate: moment(w.dateFromUTC).toDate(),
            Duration: moment
              .duration(moment(w.dateToUTC).diff(moment(w.dateFromUTC)))
              .asDays(),
          })),
        };
      }),
    });
  });

  projectWithoutTag.forEach((p) => {
    let projectWorkSessions = activeWorkSessions.filter(
      (w) => w.projectID === p.id
    );
    projectWorkSessions = projectWorkSessions.filter((w) => !w.isTBD());
    projectWorkSessions.sort(
      (a, b) =>
        moment(a.dateFromUTC).valueOf() - moment(b.dateFromUTC).valueOf()
    );

    data.push({
      TaskID: `Project_${p.id}`,
      Data: p,
      Color: "#2196f3",
      TaskName: p.name,
      subtasks: projectWorkSessions.map((w) => ({
        TaskID: `WorkSession_${w.id}`,
        TaskName: `${w.title}`,
        Data: w,
        Color: "#4caf50",
        StartDate: moment(w.dateFromUTC).toDate(),
        Duration: moment
          .duration(moment(w.dateToUTC).diff(moment(w.dateFromUTC)))
          .asDays(),
      })),
    });
  });

  const toolbarOptions = [
    "ExpandAll",
    "CollapseAll",
    "ZoomIn",
    "ZoomOut",
    "ZoomToFit",
    "Search",
  ];

  function TaskbarTemplate(props) {
    let background = props?.taskData?.Data?.color;
    if (!background)
      background = props?.taskData?.Data?.projectColor ?? "#ff9800";
    return (
      <Box
        sx={{
          background: background,
          width: "100%",
          minWidth: "10px",
          borderRadius: 4,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      ></Box>
    );
  }
  function ParentTaskbarTemplate(props) {
    let background = props?.taskData?.Data?.color;
    if (!background)
      background = props?.taskData?.Data?.projectColor ?? "#2196f3";
    return (
      <Box
        sx={{
          background: background,
          width: "100%",
          minWidth: "10px",
          borderRadius: 4,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      ></Box>
    );
  }
  function MilestoneTemplate(props) {
    return <Box className="e-gantt-milestone"></Box>;
  }

  function toolbarClick(args) {
    if (args.item.id === "Default_excelexport") {
      (ref as any).current.excelExport();
    } else if (args.item.id === "Default_csvexport") {
      (ref as any).current.csvExport();
    } else if (args.item.id === "Default_pdfexport") {
      (ref as any).current.pdfExport();
    }
  }

  const todayMarker = moment().toDate();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 2,
        flexDirection: "column",
        justifyContent: "stretch",
        margin: "auto",
        width: "100%",
        position: "relative",
        flex: 1,
        height: heightWithToolbar,
      }}
    >
      <GanttComponent
        ref={ref}
        tooltipSettings={{ showTooltip: false }}
        taskbarTemplate={TaskbarTemplate}
        parentTaskbarTemplate={ParentTaskbarTemplate}
        allowExcelExport={true}
        allowPdfExport={true}
        // milestoneTemplate={MilestoneTemplate}
        toolbarClick={toolbarClick}
        id="Default"
        queryTaskbarInfo={(args) => {
          if (args.data.Color) {
            args.taskbarBgColor = args.data.Color;
            args.progressBarBgColor = args.data.Color;
          }
        }}
        dataSource={data}
        highlightWeekends={true}
        treeColumnIndex={1}
        taskFields={taskFields}
        recordDoubleClick={(e) => {
          if (e.rowData.TaskID.includes("Tag")) {
            const tagID = e.rowData.TaskID.split("_")[1];
            navigate(`${RouterConfig.seasons}/${tagID}/projects`);
          }
          if (e.rowData.TaskID.includes("Project")) {
            const projectID = e.rowData.TaskID.split("_")[1];
            navigate(`${RouterConfig.projects}/${projectID}/mission-control`);
          }
          if (e.rowData.TaskID.includes("WorkSession")) {
            const workSessionID = e.rowData.TaskID.split("_")[1];
            const workSession = activeWorkSessions.find(
              (w) => `${w.id}` === workSessionID
            );
            navigate(
              `${RouterConfig.projects}/${workSession.projectID}/mission-control?workSessionID=${workSession.id}`
            );
            console.log("Redirect to WorkSession");
          }
        }}
        toolbar={toolbarOptions}
        allowSelection={true}
        labelSettings={labelSettings}
        height="100%"
        width="calc(100vw - 32px)"
      >
        <ColumnsDirective>
          <ColumnDirective field="TaskID" width="0"></ColumnDirective>
          <ColumnDirective field="TaskName" width="250px"></ColumnDirective>
          <ColumnDirective field="StartDate"></ColumnDirective>
          <ColumnDirective field="EndDate"></ColumnDirective>
        </ColumnsDirective>
        <EventMarkersDirective>
          <EventMarkerDirective
            day={todayMarker}
            cssClass="e-custom-event-marker"
            label=""
          ></EventMarkerDirective>
        </EventMarkersDirective>
        <Inject
          services={[
            Selection,
            Toolbar,
            Filter,
            ExcelExport,
            PdfExport,
            DayMarkers,
          ]}
        />
      </GanttComponent>
    </Box>
  );
}

function TooltipContent({
  project,
  tag,
  workSession,
  stagesMap,
}: {
  project?: Project;
  tag?: Tag;
  workSession?: WorkSession;
  stagesMap?: Dictionary<Stage>;
}) {
  if (workSession)
    return (
      <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 250,
        }}
      >
        <Box>
          <Typography
            startDecorator={<i className={workSession.typeOption()?.icon} />}
          >
            {workSession.title}
          </Typography>
          <Typography
            level="body2"
            endDecorator={
              <>
                <Tooltip
                  arrow
                  size="sm"
                  variant="outlined"
                  title={workSession.fromNowTooltip()}
                >
                  <Typography
                    color={workSession.fromNowColor()}
                    variant={"soft"}
                    noWrap
                    level="body5"
                  >
                    {workSession.completed() ? (
                      <i className="fa-solid fa-check"></i>
                    ) : undefined}
                    {workSession.isTBD() ? (
                      <i className="fa-solid fa-question"></i>
                    ) : undefined}
                    {!workSession.isTBD() && !workSession.completed()
                      ? workSession.fromNow()
                      : undefined}
                  </Typography>
                </Tooltip>
              </>
            }
          >
            {workSession.formatDateRange()}
          </Typography>
          <Typography level="body2"> @{workSession.studioName}</Typography>
        </Box>

        <Button
          component={Link}
          to={`${RouterConfig.projects}/${workSession.projectID}/mission-control?workSessionID=${workSession.id}`}
          fullWidth
          size="sm"
        >
          Open
        </Button>
      </Box>
    );

  if (project)
    return (
      <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 250,
        }}
      >
        <Typography
          startDecorator={
            project.logoURL ? (
              <img src={project.logoURL} style={{ width: 25, height: 25 }} />
            ) : (
              <i className="fa-solid fa-folder-open"></i>
            )
          }
        >
          {project.name}
        </Typography>
        <Box>
          <Typography level="body2">Hiring:</Typography>
          <HiringProgress
            version={project.version}
            projectID={project.id}
            stagesMap={stagesMap}
          />
        </Box>
        <Button
          component={Link}
          to={`${RouterConfig.projects}/${project.id}/mission-control`}
          fullWidth
          size="sm"
        >
          Open
        </Button>
      </Box>
    );

  return <Box></Box>;
}
